import { Box, Typography } from "@mui/material";
import React from "react";
import { tokens } from "../../../theme";

/**
 * The frame an analytics section card sits in — the card-scale twin of the
 * shell's chrome trick: ONE component, and the structural difference between
 * themes lives here, keyed on `tokens.cardChrome.variant`, never in the views.
 *
 *  - "banner" (legacy): the coloured title bar, reproduced verbatim from the
 *    original growthForecast markup — bar colour/border/radius come from the
 *    config, the geometry (5px/20px padding, h5 bold title) is the original's.
 *    Children render unwrapped: legacy content brings its own borders.
 *  - "plain" (refreshed): a surface card with a title row, following the
 *    kpiCard/preview conventions.
 *
 * Views pass title, an optional header `action` (legacy's white kebab), and
 * children. Adding a theme never touches this file — only a config.
 */
interface SectionCardProps {
  title: string;
  /** Per-card banner colour override (e.g. legacy's navy Retention Leads bar). */
  headerBg?: string;
  headerText?: string;
  /** Legacy dashboard banners navigate on click; restores that affordance. */
  onHeaderClick?: () => void;
  action?: React.ReactNode;
  children: React.ReactNode;
}

const SectionCard = ({ title, action, children, headerBg, headerText, onHeaderClick }: SectionCardProps) => {
  const chrome = tokens.cardChrome;

  if (chrome.variant === "banner") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <Box
          sx={{
            backgroundColor: headerBg ?? chrome.headerBg,
            display: "flex",
            padding: "5px 0px 5px 20px",
            alignItems: "center",
            borderRadius: `${chrome.radius}px ${chrome.radius}px 0px 0px`,
            border: chrome.headerBorder,
            justifyContent: "space-between",
            cursor: onHeaderClick ? "pointer" : "default",
          }}
          onClick={onHeaderClick}
        >
          <Typography variant="h5" sx={{ color: headerText ?? chrome.headerText, fontWeight: "bold" }}>
            {title}
          </Typography>
          {action}
        </Box>
        {children}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        backgroundColor: tokens.color.surface,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: `${chrome.radius}px`,
        boxShadow: tokens.shadow.card,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px 10px",
          cursor: onHeaderClick ? "pointer" : "default",
        }}
        onClick={onHeaderClick}
      >
        <Typography
          sx={{
            fontSize: tokens.typography.cardTitle,
            fontWeight: tokens.typography.headingWeight,
            color: headerText ?? chrome.headerText,
          }}
        >
          {title}
        </Typography>
        {action}
      </Box>
      <Box sx={{ padding: "0 16px 16px" }}>{children}</Box>
    </Box>
  );
};

export default SectionCard;
